<template>
  <!-- GWP threshold; used to remove item from cart -->
  <progress-indicator
    v-if="$variable('gwp.enabled') && typeof $variable('gwp.threshold') === 'number'"
    v-show="false"
    :threshold="$variable('gwp.threshold')"
    :total="cartTotalExcludingGift"
    @threshold-met="handleThresholdMet"
  />
  <form
    class="main-cart critical-component-clear grid"
    :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
    method="post"
  >
    <template v-if="cart.item_count > 0 || cartStatus.label === 'adding'">
      <btn
        class="main-cart__submit col xs-span"
        :label="$string('cart.checkout')"
        name="checkout"
        type="submit"
        modifiers="primary center"
      />

      <div class="main-cart__header col xs-span l6">
        <h1
          id="template-title"
          class="main-cart__title text-heading-s"
          v-text="$string('cart.title')"
        />
      </div>

      <div class="main-cart__content col xs-span grid no-row-gap">
        <div class="col xs-span l8 xl7">
          <div class="main-cart__line-items">
            <template
              v-for="(lineItem) of cartItems"
              :key="getCartItemKey(lineItem)"
            >
              <line-item
                class="main-cart__line-item"
                :line-item="lineItem"
              />

              <divider-line />
            </template>
          </div>
          <div
            v-if="promises.enabled && promises.blocks.length > 0"
            class="is-desktop"
          >
            <promise-grid
              :promises="promises"
              block-class="main-cart"
            />
          </div>
        </div>

        <aside class="main-cart__sidebar col xs-span l10-12 xl9-12">
          <cart-summary
            class="main-cart__summary"
          >
            <template #express-checkout>
              <slot name="express-checkout" />
            </template>
          </cart-summary>
        </aside>
        <div
          v-if="promises.enabled && promises.blocks.length > 0"
          class="is-mobile col xs-span l10-12 xl9-12"
        >
          <promise-grid
            :promises="promises"
            block-class="main-cart"
          />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="main-cart__header col xs-span l6">
        <h1
          id="template-title"
          class="main-cart__title text-heading-s"
          v-text="$string('cart.empty.title')"
        />

        <p
          class="
            main-cart__text
            text-body-m-regular
            text-body-l-regular-desktop
          "
          v-html="getTextHtml($string('cart.empty.text_html'))"
        />

        <div
          v-if="empty.hasEmptyCartButton"
          class="main-cart__empty-cta-container"
        >
          <btn
            v-if="empty.ctaText && empty.ctaUrl"
            class="main-cart__empty-cta"
            :label="empty.ctaText"
            :url="empty.ctaUrl"
          />
          <btn
            v-if="empty.ctaText2 && empty.ctaUrl2"
            class="main-cart__empty-cta"
            :label="empty.ctaText2"
            :url="empty.ctaUrl2"
          />
        </div>
      </div>
    </template>
  </form>
</template>

<script>

/**
 * Vue: Main cart (main-cart)
 * -----------------------------------------------------------------------------
 * Cart page with line items.
 *
 * @param {Object} empty - Empty cart CTA settings.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import LineItem from '~async/line-item/line-item'
import ProgressIndicator from '~async/progress-indicator/progress-indicator'
import PromiseGrid from '~async/main-cart/promise-grid'

export default {
  name: 'MainCart',

  components: {
    Btn,
    CartSummary,
    DividerLine,
    LineItem,
    ProgressIndicator,
    PromiseGrid,
  },

  props: {
    empty: {
      type: Object,
      default: () => ({
        ctaText: '',
        ctaUrl: '',
        ctaText2: '',
        ctaUrl2: '',
        hasEmptyCartButton: false,
      }),
    },
    promises: {
      type: Object,
      default: () => ({
        enabled: false,
        title: '',
        blocks: [],
      }),
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      giftLineItem: 'cart/getGiftLineItem',
      cartTotalExcludingGift: 'cart/getCartTotalExcludingGift',
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },

  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      removeItem: 'cart/updateItem',
    }),

    /**
     * Handle threshold met event.
     * @param {Boolean} isMet - Is threshold met.
     * @returns {Boolean}
     */
    handleThresholdMet(isMet) {
      if (!isMet) {
        this.handleRemoveGiftItem()
      }
    },

    /**
     * Handle remove gift item.
     */
    handleRemoveGiftItem() {
      if (!this.giftLineItem) {
        return
      }
      this.removeItem(this.giftLineItem.key)
    },

    /**
     * Get text HTML.
     * @param {String} string - Text to format.
     * @returns {String}
     */
    getTextHtml(string) {
      return string
        .replace('\n', '<br>')
        .replaceAll('<a', '<a class="text-body-m-underline text-body-l-underline-desktop"')
    },

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @returns {String}
     */
    getCartItemKey(item) {
      if (!item) {
        return 'cart-line-item-loading'
      }

      return `cart-line-item-${item.key}`
    },
  },
}
</script>

<style lang="scss">
@import './main-cart';
</style>
